import { useState } from 'react'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { ladderBody } from '@/settings/follow-ups/channels'
import type { Step } from '@/settings/follow-ups/channels'
import { FollowUpsForm } from '@/settings/follow-ups/form'
import { summariseFollowUps } from '@/settings/follow-ups/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { useOneEditing } from '@/ui/useOneEditing'

/**
 * Settings › Outreach › Follow-ups.
 *
 * The ladder and the daily cap were two cards on Outreach › What we say until
 * the settings consolidation's second batch (2026-09-15). They are read from
 * `GET /outreach/brief` because that is the only read that carries them — the
 * brief itself stays on the compose page. Two writes on Save, ladder then cap,
 * in the order the compose page always made them; the cap is skipped when it
 * did not change, as it was there.
 */
export function FollowUpsPanel(): JSX.Element {
  const { membership } = useActiveTenant()
  const editable = may(membership, 'outreach:manage')
  const toast = useToast()
  const one = useOneEditing()
  const [savedAt, setSavedAt] = useState<string | null>(null)

  const compose = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/brief', { signal })),
    [],
  )
  const saveLadder = useMutation((steps: Step[]) =>
    call(() => api.PUT('/api/v1/outreach/follow-ups/ladder', { body: ladderBody(steps) })),
  )
  const saveCap = useMutation((value: number) =>
    call(() => api.PUT('/api/v1/outreach/follow-ups/cap', { body: { follow_ups_per_day: value } })),
  )

  if (compose.state.status === 'loading')
    return <Loading label="Loading your follow-ups" lines={2} />
  if (compose.state.status === 'error') {
    return (
      <ErrorState
        failure={compose.state.failure}
        title="Could not load your follow-ups"
        onRetry={compose.refetch}
      />
    )
  }
  const current = compose.state.data

  return (
    <div className="ar-stack">
      <h2 className="ar-h2">Follow-ups</h2>
      <SettingRow
        label="Follow-ups"
        summary={summariseFollowUps(current.steps, current.daily_cap)}
        savedAt={savedAt}
        editable={editable}
        editing={one.editing === 'follow-ups'}
        onEdit={() => {
          one.open('follow-ups')
        }}
        onCancel={one.close}
      >
        <FollowUpsForm
          value={{ steps: current.steps, cap: String(current.daily_cap) }}
          submitLabel="Save"
          onSubmit={async (next) => {
            const ladder = await saveLadder.run(next.steps)
            if (!ladder.ok) return ladder
            if (next.cap !== current.daily_cap) {
              const capped = await saveCap.run(next.cap)
              if (!capped.ok) return capped
            }
            setSavedAt(new Date().toISOString())
            one.close()
            compose.refetch()
            toast.show({
              kind: 'success',
              title: 'Follow-ups saved',
              body:
                ladder.data.cancelled > 0
                  ? `${String(ladder.data.cancelled)} queued follow-ups on rungs you removed were cancelled.`
                  : `Up to ${String(next.cap)} a day, inside your send window.`,
            })
            return ladder
          }}
        />
      </SettingRow>
    </div>
  )
}
